import { ArrowUpCircleIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";
import AddButton from "../../../../components/AddButton";
import DownloadButton from "../../../../components/DownloadButton";
import MassUploadButton from "../../../../components/MassUploadButton";
import { useRecoilState, useRecoilValue } from "recoil";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import {
  carrierAddState,
  carrierContactMassUploadState,
  carrierMassUploadState,
} from "../atom/atoms";
import { toast } from "../../../../components/toastNotification";
import { ICarriers } from "../types";
import * as XLSX from "xlsx";
import useForCarrierDownload from "../hooks/useForCarrierDownload";
interface CarrierActionGroupProps {
  carriersData?: Array<ICarriers>;
}

export const CarrierActionGroup = ({
  carriersData = [],
}: CarrierActionGroupProps): JSX.Element => {
  const userPermissions = useRecoilValue(permissionsSelector);
  const addPermission =
    PERMISSIONS.permission_carrieragreemnentmgmt_carrier_add;

  const [, setAddStatus] = useRecoilState(carrierAddState);
  const [, setCarrierMassUploadModal] = useRecoilState(carrierMassUploadState);
  const [, setContactMassUploadModal] = useRecoilState(
    carrierContactMassUploadState
  );
  const [isSuccess, setIsSuccess] = useState(false);
  const [exportCarrierDetails, setExportCarrierDetails] = useState([]);
  const [exportCarrierMetrics, setExportCarrierMetrics] = useState([]);
  const [exportStateAppointed, setExportStateAppointed] = useState([]);
  const [exportNationalContact, setExportNationalContact] = useState([]);

  const downloadRef = useRef<any>();
  const downloadCarriers = () => {
    downloadRef && downloadRef.current?.link?.click();
    toast.fire(
      "Download Agreement ",
      "Successfully Downloaded Carriers",
      "success"
    );
  };

  const {
    mutate: getCarrierDetails,
    isLoading: getCarrierDetailsIsLoading,
    isSuccess: downloadSuccess,
  } = useForCarrierDownload();

  const handleDownload = () => {
    const ids = carriersData?.map((x) => x.masterCarrierId);

    getCarrierDetails(ids?.join(",").split(","), {
      onSuccess: (res: any) => {
        if (res) {
          setExportCarrierDetails(
            res.carrierDetailsList?.map((x) => {
              return {
                "Master Carrier Id": x.masterCarrierId,
                "Carrier Name": x.carrierName,
                Alias: x.masterCarrierDetails?.alias,
                Address: x.masterCarrierDetails?.masterCarrierAddress,
                NSA: x.masterCarrierDetails?.nsa ? "True" : "False",
                SPLC: x.masterCarrierDetails?.splc ? "True" : "False",
                FEIN: x.fein,
                NAIC: x.naic,
                "A.M Best Rating": x.amBestRating,
                "Date of A.M Best Rating": x.dateOfAmBestRating
                  ? new Date(x.dateOfAmBestRating).toISOString().split("T")[0]
                  : x.dateOfAmBestRating,
                Active: x.isActive ? "True" : "False",
                "Created By": x.createdBy,
                "Created On": x.createdOn
                  ? new Date(x.createdOn).toISOString().split("T")[0]
                  : x.createdOn,
                "Updated By": x.updatedBy,
                "Updated On": x.updatedOn
                  ? new Date(x.updatedOn).toISOString().split("T")[0]
                  : x.updatedOn,
                IsDigitized: x.isDigitized ? "True" : "False",
              };
            })
          );
          setExportCarrierMetrics(
            res.carrierMetricsList?.map((x) => {
              return {
                CarrierName: x.carrierName,
                States: x.states,
                TotalPIFCount: x.totalPIFCount,
                TotalPIFPremium: x.totalPIFPremium,
                NewBusinessPremium: x.newBusinessPremium,
                RenewalPremium: x.renewalPremium,
              };
            })
          );
          setExportStateAppointed(
            res.statesAppointedList?.map((x) => {
              return {
                CarrierName: x.carrierName,
                States: x.states,
                NewBusiness: x.newBusiness ? "Yes" : "No",
                RenewalBusiness: x.renewalBusiness ? "Yes" : "No",
                NewBusinessSuspended: x.newBusinessSuspended ? "Yes" : "No",
                NewBusinessSuspendedDate: x.newBusinessSuspendedDate
                  ? new Date(x.newBusinessSuspendedDate)?.toLocaleDateString()
                  : "",
              };
            })
          );
          setExportNationalContact(
            res.nationaContactsList?.map((x) => {
              return {
                CarrierName: x.carrierName,
                Name: x.name,
                Email: x.email,
                ContactNumber: x.contactNumber,
                Address: x.address,
                ContactType: x.contactType,
                PhoneType: x.phoneType,
                ExtensionNumber: x.extensionNumber,
              };
            })
          );
          downloadRef && downloadRef.current?.link?.click();
          toast.fire(
            "Download Carrier ",
            "Successfully Downloaded Carriers",
            "success"
          );
          setIsSuccess(true);
        }
      },
      // Toaster on Error
      onError: (error) => {
        if (error) {
          toast.fire(
            "Download Carrier",
            "Server Error: Cannot download Carriers.",
            "error"
          );
        }
      },
    });
  };

  useEffect(() => {
    if (isSuccess) {
      // For getting security details
      const carrierDetails = XLSX.utils.json_to_sheet(exportCarrierDetails);
      const carrierMetrics = XLSX.utils.json_to_sheet(exportCarrierMetrics);
      const stateAppointed = XLSX.utils.json_to_sheet(exportStateAppointed);
      const nationalContact = XLSX.utils.json_to_sheet(exportNationalContact);

      const wb = XLSX.utils.book_new();

      // For additional sheet in excel
      const carrierSheet = XLSX.utils.book_append_sheet(
        wb,
        carrierDetails,
        "Carrier"
      );
      const metricsSheet = XLSX.utils.book_append_sheet(
        wb,
        carrierMetrics,
        "Carrier Metric Per State"
      );
      const stateAppointedSheet = XLSX.utils.book_append_sheet(
        wb,
        stateAppointed,
        "State Appointed"
      );
      const nationalContactSheet = XLSX.utils.book_append_sheet(
        wb,
        nationalContact,
        "National Contact"
      );
      XLSX.writeFile(
        wb,
        "Carriers_" + new Date()?.toLocaleDateString() + ".xlsx"
      );
      setIsSuccess(false);
    }
  }, [isSuccess]);

  return (
    <div className="flex justify-between flex-row text-xs items-center w-full">
      <div className="gap-2 flex items-center">
        {userPermissions.includes(addPermission) && (
          <>
            <AddButton buttonText="Add" onClick={() => setAddStatus(true)} />{" "}
            <MassUploadButton
              onClickFunction={() => {
                setCarrierMassUploadModal(true);
              }}
            >
              <ArrowUpCircleIcon className="h-4 w-4 mr-2" />
              <p>Carrier Mass Upload</p>
            </MassUploadButton>
            <MassUploadButton
              onClickFunction={() => {
                setContactMassUploadModal(true);
              }}
            >
              <ArrowUpCircleIcon className="h-4 w-4 mr-2" />
              <p> Contact Mass Upload</p>
            </MassUploadButton>
          </>
        )}
        <div className="box-content">
          <DownloadButton
            buttonValue={
              getCarrierDetailsIsLoading ? "Downloading..." : "Download File"
            }
            onClick={() => handleDownload()}
          />
          {/* <CSVLink
            className="2xl:-mt-1 hidden"
            ref={downloadRef}
            {...csvReport}
          ></CSVLink> */}
        </div>
      </div>
    </div>
  );
};

export default CarrierActionGroup;
